import { useEffect, useMemo, useState } from 'react';
import { Search, Shield, UserPlus, Users, AlertTriangle } from 'lucide-react';
import { rolesAPI, employeesAPI } from '../services/api';
import { InlineLoader } from './loading';

/**
 * Who can see a session or a learning journey, chosen inline on the editor
 * that creates it.
 *
 * Both editors previously shipped only the `audience` dropdown, and the moment
 * it was switched to 'assigned' the thing became invisible to everyone: the
 * only way to actually name a person was to save, return to the list, open a
 * kebab menu and visit a separate assignments page. Every admin who set
 * "Only assigned employees and roles" and pressed Save published something no
 * learner could open.
 *
 * The picker unions two channels, exactly as the backend's visibility SQL does
 * (`a.employee_code = $2 OR ur.role_id IS NOT NULL`):
 *   - roles      → everyone currently holding that role in this project
 *   - employees  → named individuals
 * Because it is a union, an employee already covered by a selected role adds
 * nobody; those rows are shown ticked and locked with the role that covers
 * them, so the count on screen is the real reach rather than a sum with
 * double-counting in it.
 */

export type LmsAudience = 'everyone' | 'assigned';

export interface LmsAudiencePickerProps {
  projectId?: number;
  /** Lower-case noun used in the help text, e.g. 'session' | 'journey'. */
  targetLabel: string;
  audience: LmsAudience;
  onAudienceChange: (audience: LmsAudience) => void;
  roleIds: string[];
  onRoleIdsChange: (roleIds: string[]) => void;
  employeeCodes: string[];
  onEmployeeCodesChange: (codes: string[]) => void;
  disabled?: boolean;
}

interface RoleOption {
  id: string;
  name: string;
  category?: string | null;
}

interface EmployeeOption {
  code: string;
  name: string;
  designation?: string | null;
}

type RawEmployee = Record<string, unknown>;

const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim();

const readEmployeeCode = (emp: RawEmployee): string =>
  str(emp.emp_code) || str(emp.empCode) || str(emp.employeeCode) || str(emp.employee_code) || str(emp.id);

const readEmployeeName = (emp: RawEmployee): string =>
  str(emp.emp_name) || str(emp.empName) || str(emp.employeeName) || str(emp.name);

/**
 * The employee list is a full project roster — thousands of rows on the larger
 * projects. Rendering every one of them freezes the editor, so the list is
 * capped and the search box is the way down to a specific person. "Select all"
 * deliberately operates on the whole filtered set, not the visible slice, so
 * the cap never silently narrows what an admin just assigned.
 */
const MAX_VISIBLE_EMPLOYEES = 200;

export default function LmsAudiencePicker({
  projectId,
  targetLabel,
  audience,
  onAudienceChange,
  roleIds,
  onRoleIdsChange,
  employeeCodes,
  onEmployeeCodesChange,
  disabled = false,
}: LmsAudiencePickerProps) {
  const [activeTab, setActiveTab] = useState<'roles' | 'employees'>('roles');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  /** employee code -> role ids currently held, from user_roles. */
  const [roleMembers, setRoleMembers] = useState<Record<string, string[]>>({});
  const [roleSearch, setRoleSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');

  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const [rolesResponse, employeesResponse, roleAssignmentsResponse] = await Promise.all([
          rolesAPI.getByProject(projectId),
          employeesAPI.getProjectEmployees(true, projectId),
          rolesAPI.getAllAssignments(projectId),
        ]);
        if (cancelled) return;

        setRoles(rolesResponse.data?.data || []);

        // getProjectEmployees answers { data: { deployed, undeployed, all } };
        // older deployments answer a bare array. Accept both.
        const employeesPayload = employeesResponse.data?.data;
        const rawEmployees: RawEmployee[] = Array.isArray(employeesPayload)
          ? employeesPayload
          : employeesPayload?.all || [];

        const seen = new Set<string>();
        const normalized: EmployeeOption[] = [];
        rawEmployees.forEach((emp) => {
          const code = readEmployeeCode(emp);
          if (!code || seen.has(code)) return;
          seen.add(code);
          normalized.push({
            code,
            name: readEmployeeName(emp) || code,
            designation: str(emp.designation) || null,
          });
        });
        normalized.sort((a, b) => a.name.localeCompare(b.name));
        setEmployees(normalized);

        const membership: Record<string, string[]> = {};
        (roleAssignmentsResponse.data?.data || []).forEach((row: RawEmployee) => {
          const code = str(row.user_name);
          const roleId = str(row.role_id);
          if (!code || !roleId) return;
          (membership[code] ||= []).push(roleId);
        });
        setRoleMembers(membership);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading audience options:', err);
        setLoadError('Could not load roles and employees. Reload the page to try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const roleNameById = useMemo(() => {
    const map: Record<string, string> = {};
    roles.forEach((role) => {
      map[role.id] = role.name;
    });
    return map;
  }, [roles]);

  const selectedRoleSet = useMemo(() => new Set(roleIds), [roleIds]);
  const selectedEmployeeSet = useMemo(() => new Set(employeeCodes), [employeeCodes]);

  /** How many people currently hold each role, for the count on each row. */
  const memberCountByRole = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.values(roleMembers).forEach((held) => {
      held.forEach((roleId) => {
        counts[roleId] = (counts[roleId] || 0) + 1;
      });
    });
    return counts;
  }, [roleMembers]);

  /** The role that already covers this employee, if any — null when pickable. */
  const coveringRoleName = (code: string): string | null => {
    const held = roleMembers[code];
    if (!held) return null;
    const covering = held.find((roleId) => selectedRoleSet.has(roleId));
    return covering ? roleNameById[covering] || 'a selected role' : null;
  };

  const filteredRoles = useMemo(() => {
    const term = roleSearch.trim().toLowerCase();
    if (!term) return roles;
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(term) ||
        (role.category || '').toLowerCase().includes(term)
    );
  }, [roles, roleSearch]);

  const filteredEmployees = useMemo(() => {
    const term = employeeSearch.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter(
      (emp) =>
        emp.name.toLowerCase().includes(term) ||
        emp.code.toLowerCase().includes(term) ||
        (emp.designation || '').toLowerCase().includes(term)
    );
  }, [employees, employeeSearch]);

  /**
   * Employees reached through a selected role. Kept separate from the directly
   * picked ones so the summary reports a union, not a sum.
   */
  const reachedViaRoles = useMemo(() => {
    const codes = new Set<string>();
    if (selectedRoleSet.size === 0) return codes;
    Object.entries(roleMembers).forEach(([code, held]) => {
      if (held.some((roleId) => selectedRoleSet.has(roleId))) codes.add(code);
    });
    return codes;
  }, [roleMembers, selectedRoleSet]);

  const directOnlyCount = useMemo(
    () => employeeCodes.filter((code) => !reachedViaRoles.has(code)).length,
    [employeeCodes, reachedViaRoles]
  );
  const totalReach = reachedViaRoles.size + directOnlyCount;

  const toggleRole = (roleId: string) => {
    if (disabled) return;
    onRoleIdsChange(
      selectedRoleSet.has(roleId) ? roleIds.filter((id) => id !== roleId) : [...roleIds, roleId]
    );
  };

  const toggleEmployee = (code: string) => {
    if (disabled || coveringRoleName(code)) return;
    onEmployeeCodesChange(
      selectedEmployeeSet.has(code)
        ? employeeCodes.filter((c) => c !== code)
        : [...employeeCodes, code]
    );
  };

  const allFilteredRolesSelected =
    filteredRoles.length > 0 && filteredRoles.every((role) => selectedRoleSet.has(role.id));

  const toggleAllRoles = () => {
    if (disabled) return;
    const filteredIds = filteredRoles.map((role) => role.id);
    if (allFilteredRolesSelected) {
      const drop = new Set(filteredIds);
      onRoleIdsChange(roleIds.filter((id) => !drop.has(id)));
    } else {
      onRoleIdsChange(Array.from(new Set([...roleIds, ...filteredIds])));
    }
  };

  /**
   * Select-all skips anyone a selected role already covers — adding them
   * individually would be a no-op the admin then has to un-tick one by one.
   */
  const selectableFilteredEmployees = useMemo(
    () => filteredEmployees.filter((emp) => !coveringRoleName(emp.code)),
    // coveringRoleName reads selectedRoleSet and roleMembers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredEmployees, roleMembers, selectedRoleSet]
  );

  const allFilteredEmployeesSelected =
    selectableFilteredEmployees.length > 0 &&
    selectableFilteredEmployees.every((emp) => selectedEmployeeSet.has(emp.code));

  const toggleAllEmployees = () => {
    if (disabled) return;
    const codes = selectableFilteredEmployees.map((emp) => emp.code);
    if (allFilteredEmployeesSelected) {
      const drop = new Set(codes);
      onEmployeeCodesChange(employeeCodes.filter((code) => !drop.has(code)));
    } else {
      onEmployeeCodesChange(Array.from(new Set([...employeeCodes, ...codes])));
    }
  };

  const clearAll = () => {
    if (disabled) return;
    onRoleIdsChange([]);
    onEmployeeCodesChange([]);
  };

  const visibleEmployees = filteredEmployees.slice(0, MAX_VISIBLE_EMPLOYEES);
  const hiddenEmployeeCount = filteredEmployees.length - visibleEmployees.length;
  const hasSelection = roleIds.length > 0 || employeeCodes.length > 0;

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Who is this for?</label>
        <select
          value={audience}
          onChange={(e) => onAudienceChange(e.target.value as LmsAudience)}
          disabled={disabled}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
        >
          <option value="everyone">Everyone in the project</option>
          <option value="assigned">Only assigned employees and roles</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {audience === 'assigned'
            ? `Only the roles and people you pick below can see this ${targetLabel}.`
            : `Every learner in the project can see this ${targetLabel}.`}
        </p>
      </div>

      {/* Assignments are saved either way, so an admin can line up an audience
          before switching over — but silently keeping a restricted list on an
          open item is the kind of thing you discover a month later. */}
      {audience === 'everyone' && hasSelection && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">
            You have picked {roleIds.length} role{roleIds.length === 1 ? '' : 's'} and{' '}
            {employeeCodes.length} employee{employeeCodes.length === 1 ? '' : 's'}, but this{' '}
            {targetLabel} is open to everyone. Switch to{' '}
            <span className="font-medium">Only assigned employees and roles</span> to restrict it —
            your picks are saved either way.
          </p>
        </div>
      )}

      {audience === 'assigned' && (
        <div className="rounded-lg border border-gray-200">
          <div className="flex items-center justify-between border-b border-gray-200 px-3">
            <div className="flex">
              <button
                type="button"
                onClick={() => setActiveTab('roles')}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'roles'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Shield className="w-4 h-4 inline-block mr-1.5" />
                Roles
                {roleIds.length > 0 && (
                  <span className="ml-1.5 rounded-full bg-primary-100 px-1.5 py-0.5 text-xs text-primary-700">
                    {roleIds.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('employees')}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'employees'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <UserPlus className="w-4 h-4 inline-block mr-1.5" />
                Employees
                {employeeCodes.length > 0 && (
                  <span className="ml-1.5 rounded-full bg-primary-100 px-1.5 py-0.5 text-xs text-primary-700">
                    {employeeCodes.length}
                  </span>
                )}
              </button>
            </div>
            {hasSelection && (
              <button
                type="button"
                onClick={clearAll}
                disabled={disabled}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="p-3 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                <InlineLoader label="Loading roles and employees…" />
              </div>
            ) : loadError ? (
              <p className="py-4 text-center text-sm text-red-600">{loadError}</p>
            ) : activeTab === 'roles' ? (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    placeholder="Search roles…"
                    className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-gray-50 px-2 py-1.5">
                  <input
                    type="checkbox"
                    checked={allFilteredRolesSelected}
                    onChange={toggleAllRoles}
                    disabled={disabled || filteredRoles.length === 0}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select all {filteredRoles.length} role{filteredRoles.length === 1 ? '' : 's'}
                    {roleSearch.trim() ? ' matching your search' : ''}
                  </span>
                </label>

                <div className="max-h-56 space-y-0.5 overflow-y-auto">
                  {filteredRoles.length === 0 ? (
                    <p className="py-4 text-center text-sm text-gray-500">No roles found</p>
                  ) : (
                    filteredRoles.map((role) => (
                      <label
                        key={role.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRoleSet.has(role.id)}
                          onChange={() => toggleRole(role.id)}
                          disabled={disabled}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="min-w-0 flex-1 text-sm text-gray-800">
                          {role.name}
                          {role.category && (
                            <span className="ml-1.5 text-xs text-gray-400">{role.category}</span>
                          )}
                        </span>
                        <span className="shrink-0 text-xs text-gray-500">
                          {memberCountByRole[role.id] || 0} member
                          {(memberCountByRole[role.id] || 0) === 1 ? '' : 's'}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    placeholder="Search employees by name or code…"
                    className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-gray-50 px-2 py-1.5">
                  <input
                    type="checkbox"
                    checked={allFilteredEmployeesSelected}
                    onChange={toggleAllEmployees}
                    disabled={disabled || selectableFilteredEmployees.length === 0}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select all {selectableFilteredEmployees.length} employee
                    {selectableFilteredEmployees.length === 1 ? '' : 's'}
                    {employeeSearch.trim() ? ' matching your search' : ''}
                  </span>
                </label>

                <div className="max-h-56 space-y-0.5 overflow-y-auto">
                  {visibleEmployees.length === 0 ? (
                    <p className="py-4 text-center text-sm text-gray-500">No employees found</p>
                  ) : (
                    visibleEmployees.map((emp) => {
                      const covering = coveringRoleName(emp.code);
                      return (
                        <label
                          key={emp.code}
                          className={`flex items-center gap-2 rounded px-2 py-1.5 ${
                            covering ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(covering) || selectedEmployeeSet.has(emp.code)}
                            onChange={() => toggleEmployee(emp.code)}
                            disabled={disabled || Boolean(covering)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="min-w-0 flex-1 text-sm text-gray-800">
                            {emp.name}
                            <span className="ml-1.5 text-xs text-gray-400">{emp.code}</span>
                            {covering && (
                              <span className="ml-1.5 text-xs italic text-gray-400">
                                already covered by {covering}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

                {hiddenEmployeeCount > 0 && (
                  <p className="text-xs text-gray-500">
                    Showing the first {MAX_VISIBLE_EMPLOYEES} of {filteredEmployees.length}. Search
                    to narrow the list — "Select all" still covers every one of the{' '}
                    {selectableFilteredEmployees.length} matches.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2">
            <Users className="w-4 h-4 text-gray-500" />
            <p className="text-xs text-gray-600">
              {totalReach === 0 ? (
                <span className="text-amber-700">
                  Nobody assigned yet — no learner can see this {targetLabel}.
                </span>
              ) : (
                <>
                  Reaches <span className="font-medium text-gray-900">{totalReach}</span> learner
                  {totalReach === 1 ? '' : 's'}
                  {reachedViaRoles.size > 0 && ` — ${reachedViaRoles.size} via role`}
                  {reachedViaRoles.size > 0 && directOnlyCount > 0 && `, ${directOnlyCount} named directly`}
                  {reachedViaRoles.size === 0 && directOnlyCount > 0 && ' named directly'}
                </>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
